import * as React from "react";
import { View } from "react-native";
import { useRouter } from "expo-router";
import { omit } from "lodash";
import { useIsFetching } from "react-query";

import { useAccountActions } from "~/components/AccountActions/context";
import { ResponsiveRenderer } from "~/components/ResponsiveRenderer";
import { AddNote } from "~/components/SaveForm/AddNote";
import { SaveFormBreadcrumbs } from "~/components/SaveForm/Breadcrumbs";
import { useSaveFormContext } from "~/components/SaveForm/shared";
import { URLInput } from "~/components/SaveForm/URLInput";
import { backlogSlug } from "~/app/(app)/(authenticated)/[account]/(account)/backlog";
import { useCurrentAccount } from "~/hooks/useCurrentAccount";
import { isWebDesktop, useHackyParams } from "~/lib/constants";
import { useAccountStore } from "~/stores/space";
import { Button } from "~/ui/Button";
import { FilePreviewCard } from "./FilePreviewCard";
import { MediaPreviewCard } from "./MediaPreviewCard";

export default function SaveFormAddURL() {
  const account = useCurrentAccount();
  const { store } = useAccountStore();
  const { push } = useRouter();
  const { setValue, getValues, formState, handleSubmit } = useSaveFormContext();
  const params = useHackyParams<{ space: string }>();

  const saveType = getValues("type");

  const isUpdating = getValues("intent") === "update";

  const { removeModalByType } = useAccountActions();

  const isUploading =
    useIsFetching({
      queryKey: ["media_upload"],
    }) !== 0;

  const isScraping =
    useIsFetching({
      queryKey: ["scrape"],
    }) !== 0;

  const saveToBacklogButton = isUpdating ? null : (
    <Button
      isLoading={formState.isSubmitting || isUploading || isScraping}
      onPress={handleSubmit((data) => {
        const backlogId = store.spaceBySlug(backlogSlug)?.id ?? "";
        if (!backlogId) throw new Error("no backlog space");

        const commonData = {
          title: data.title,
          description: data.description,
          id: data.id,
        };

        switch (data.type) {
          case "URL": {
            store.createSave({
              spaceId: backlogId,
              set: {
                ...commonData,
                type: data.type,
                url: data.url,
                meta: data.urlMeta,
              },
            });
            break;
          }

          case "MEDIA": {
            store.createSave({
              spaceId: backlogId,
              set: {
                ...commonData,
                type: data.type,
                r2Key: data.r2Key,
                meta: omit(data.mediaMeta, "uri"),
              },
            });
            break;
          }

          case "FILE": {
            store.createSave({
              spaceId: backlogId,
              set: {
                ...commonData,
                type: data.type,
                r2Key: data.r2Key,
                meta: omit(data.fileMeta, ["uri", "file"]),
              },
            });
            break;
          }

          case "NOTE": {
            store.createSave({
              spaceId: backlogId,
              set: {
                ...commonData,
                type: data.type,
                richTextAST: data.richTextAST,
              },
            });
          }
        }

        removeModalByType("addSave");

        push({
          pathname: "/(app)/(authenticated)/[account]/(space)/[space]/saves",
          params: {
            account: account.slug,
            space: backlogSlug,
          },
        });
      })}
      variant={!params.space ? "primary" : "outline"}
    >
      {(() => {
        if (isScraping) return "Scraping...";
        if (isUploading) return "Uploading...";
        return "Save to backlog";
      })()}
    </Button>
  );

  const continueButton = (
    <Button
      isLoading={formState.isSubmitting || isUploading || isScraping}
      variant={params.space ? "primary" : "outline"}
      onPress={handleSubmit(() => {
        if (isWebDesktop()) {
          // const richTextAST = getValues("richTextAST");
          // if (shouldUpdateField("title") && !isEmpty(richTextAST)) {
          //   setValue("title", getTiptapText(richTextAST).split("\n").at(0));
          // }
          return setValue("step", "selectLocation");
        }
        push({
          pathname: "/(app)/(authenticated)/[account]/link/selectLocation",
          params: {
            account: account.slug,
          },
        });
      })}
    >
      {(() => {
        if (isScraping) return "Scraping...";
        if (isUploading) return "Uploading...";
        return "Continue";
      })()}
    </Button>
  );

  return (
    <>
      <View className="gap-y-4 px-4">
        <ResponsiveRenderer
          webDesktop={<SaveFormBreadcrumbs />}
          default={<></>}
        />
        <View />
        {(() => {
          if (saveType === "URL") {
            return <URLInput />;
          }

          if (saveType === "MEDIA") {
            return <MediaPreviewCard />;
          }

          if (saveType === "FILE") {
            return <FilePreviewCard />;
          }

          if (saveType === "NOTE") {
            return <AddNote />;
          }
        })()}

        <View />
        {params.space ? (
          <>
            {continueButton}
            {saveToBacklogButton}
          </>
        ) : (
          <>
            {saveToBacklogButton}
            {continueButton}
          </>
        )}
        <View />
      </View>
    </>
  );
}
